import { Either, Maybe } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description План уведомления.
 * @param id
 * @param title
 * @param fragments
 * @param event
 * @param channel
 * @param dispatchRouting
 * @param dispatchTime
 * @param periodLastVisit
 * @param sexId
 * @param repeatable
 * @param service
 * @param funnel
 * @param stage
 * @param isEnableAutoConfirm
 * @param textConfirm
 * @param textRefuse
 * @param sendOnWeekends
 * @constructor
 */
export function NotificationPlan(
  id,
  title = '',
  fragments = [],
  event = 'upcoming-visit',
  channel = '',
  dispatchRouting = [],
  dispatchTime = '',
  periodLastVisit = [],
  sexId = '',
  repeatable = false,
  service = '',
  funnel = '',
  stage = '',
  isEnableAutoConfirm = false,
  textConfirm = '',
  textRefuse = '',
  sendOnWeekends = true,
) {
  this.id = id;
  this.title = title;
  this.fragments = fragments;
  this.event = event;
  this.channel = channel;
  this.dispatchRouting = dispatchRouting;
  this.dispatchTime = dispatchTime;
  this.periodLastVisit = periodLastVisit;
  this.sexId = sexId;
  this.repeatable = repeatable;
  this.service = service;
  this.funnel = funnel;
  this.stage = stage;
  this.isEnableAutoConfirm = isEnableAutoConfirm;
  this.textConfirm = textConfirm;
  this.textRefuse = textRefuse;
  this.sendOnWeekends = sendOnWeekends;
}

/**
 * @description Запрос создания плана уведомлений.
 * @param id
 * @param title
 * @param fragments
 * @param event
 * @param channel
 * @param dispatchRouting
 * @param dispatchTime
 * @param periodLastVisit
 * @param sexId
 * @param repeatable
 * @param service
 * @param funnel
 * @param stage
 * @param isEnableAutoConfirm
 * @param autoConfirmTextConfirm
 * @param  autoConfirmTextRefuse
 * @param sendOnWeekends
 * @constructor
 */
export function NotificationPlanCreationRequest(
  id = null,
  title = '',
  fragments = [],
  event = '',
  channel = '',
  dispatchRouting = [],
  dispatchTime = '',
  periodLastVisit = [],
  sexId = '',
  repeatable = false,
  service = '',
  funnel = '',
  stage = '',
  isEnableAutoConfirm = false,
  autoConfirmTextConfirm = '',
  autoConfirmTextRefuse = '',
  sendOnWeekends = true,
) {
  this.id = id;
  this.title = title;
  this.fragments = fragments;
  this.event = event;
  this.channel = channel;
  this.dispatchRouting = dispatchRouting;
  this.dispatchTime = dispatchTime;
  this.periodLastVisit = periodLastVisit;
  this.sexId = sexId;
  this.repeatable = repeatable;
  this.service = service;
  this.funnel = funnel;
  this.stage = stage;
  this.isEnableAutoConfirm = isEnableAutoConfirm;
  this.autoConfirmTextConfirm = autoConfirmTextConfirm;
  this.autoConfirmTextRefuse = autoConfirmTextRefuse;
  this.sendOnWeekends = sendOnWeekends;

  this.getId = () => id;
  this.toQuery = () => ({
    title,
    fragments,
    event,
    channel,
    dispatch_time: dispatchTime,
    date_last_visit_from: periodLastVisit[0],
    date_last_visit_to: periodLastVisit[1],
    sex_id: sexId,
    repeatable,
    service,
    funnel,
    stage,
    is_enable_auto_confirm: isEnableAutoConfirm,
    auto_confirm_text_confirm: autoConfirmTextConfirm,
    auto_confirm_text_refuse: autoConfirmTextRefuse,
    send_on_weekends: sendOnWeekends,
    dispatch_routing: dispatchRouting,
  });
}

/**
 * @description Шлюз планов уведомлений.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function NotificationPlansGateway(axios) {
  /**
   * @description Получить опции.
   * @returns {Promise<any[]>}
   */
  this.getOptions = () => axios
    .get('/api/notification-plans/options')
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменение активности плана.
   * @param {string}  id    Идентификатор плана.
   * @param {boolean} value Активность.
   * @returns {Promise<Either>}
   */
  this.changeActivityPlan = (id, value) => axios
    .post(`/api/notification-plans/${id}/activity`, { activity: value })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранение нового плана.
   * @param {NotificationPlanCreationRequest} request Запрос создания.
   * @returns {Promise<Either>}
   */
  this.createPlan = (request) => axios
    .post('/api/notification-plans', request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Удаление плана.
   * @param {string} id Идентификатор плана.
   * @returns {Promise<Either>}
   */
  this.deletePlan = (id) => axios
    .delete(`/api/notification-plans/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить изменения плана.
   * @param {NotificationPlanCreationRequest} request Запрос создания.
   * @returns {Promise<Either>}
   */
  this.editPlan = (request) => axios
    .put(`/api/notification-plans/${request.getId()}`, request.toQuery())
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить список планов рассылок.
   * @returns {Promise<Either>}
   */
  this.getPlanList = () => axios
    .get('/api/notification-plans')
    .then(
      async (response) => {
        const plans = response.data.plans.map(
          (
            {
              id,
              title,
              event_name: eventName,
              channel_string: channelString,
              fired_at: firedAt,
              activity,
            },
          ) => ({
            id,
            title,
            eventName,
            channelString,
            firedAt,
            activity,
          }),
        );
        return Maybe.fromEmpty({ plans, channels: response.data.channels });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Просмотр числа уведомляемых пациентов.
   * @param {NotificationPlanCreationRequest} request
   * @returns {Promise<Either>}
   */
  this.viewNotifiablePatients = (request) => axios
    .get('/api/notification-plans/notifiable-patients', { params: request.toQuery() })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Просмотр плана.
   * @param {number} id
   * @returns {Promise<Either>}
   */
  this.viewPlan = (id) => axios
    .get(`/api/notification-plans/${id}`)
    .then(
      async (response) => Maybe.fromEmpty({
        plan: new NotificationPlan(
          response.data.id,
          response.data.title,
          response.data.fragments,
          response.data.event,
          response.data.channel,
          response.data.dispatch_routing,
          response.data.dispatch_time,
          response.data.period_last_visit.map((date) => new Date(date)),
          response.data.sex_id,
          response.data.repeatable,
          response.data.service,
          response.data.funnel,
          response.data.stage,
          response.data.is_enable_auto_confirm,
          response.data.confirm_text,
          response.data.refuse_text,
          response.data.send_on_weekends,
        ),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
